'use client';

import { useHelpWizardStore } from '@/features/products/store/helpWizard';
import { clearFilters, useSolutionStore } from '@/features/products/store/solution';
import { stepValuesToFilters } from '@/features/products/utils/stepValuesToFilters';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@andritzot/metris-web-ui/feedback/dialog';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import CheckOutlined from '@mui/icons-material/CheckOutlined';
import { useCallback, useEffect, useState } from 'react';

import Step from '../Step';
import Stepper from '../Stepper';
import { getSteps } from './utils';

type HelpDialogProps = {
  handleClose: () => void;
};

const HelpDialog = ({ handleClose }: HelpDialogProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const filtersValue = useSolutionStore((s) => s.filtersValue);
  const steps = useHelpWizardStore((s) => s.steps);

  useEffect(() => {
    if (filtersValue) {
      const stepList = getSteps(filtersValue);
      useHelpWizardStore.setState({ steps: stepList });
    }
  }, [filtersValue]);

  useEffect(() => {
    clearFilters();
  }, []);

  const clearValues = useCallback(() => {
    const stepArr = steps.map((x) => ({ ...x, values: [] }));
    useHelpWizardStore.setState({ steps: stepArr });
  }, [steps]);

  const filterProducts = useCallback(() => {
    const allValues = steps.flatMap((step) => step.values);
    useSolutionStore.setState({ filters: stepValuesToFilters(allValues) });
    clearValues();
    handleClose();
  }, [clearValues, handleClose, steps]);

  const handleGoBack = useCallback(() => {
    setActiveIndex((i) => i - 1);
  }, []);
  const handleNext = useCallback(() => {
    setActiveIndex((i) => i + 1);
  }, []);

  const hasSteps = steps.length > 0;
  const isLastStep = hasSteps && activeIndex === steps.length - 1;
  const isFirstStep = activeIndex === 0;
  const currentStepHasValues = hasSteps ? Boolean(steps[activeIndex]?.values?.length) : false;

  return (
    <Dialog
      open
      maxWidth={false}
      onClose={handleClose}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            height: '90vh',
            width: { xs: '95vw', md: '90vw' },
          },
        },
      }}
    >
      <DialogTitle title="Wizard" closable onClose={handleClose} />
      <DialogContent>
        <Box sx={{ display: 'flex', width: '100%' }}>
          <Stepper number={steps.length} activeIndex={activeIndex} />
          <Step index={activeIndex} />
        </Box>
      </DialogContent>
      <DialogActions
        tertiaryAction={
          !isFirstStep
            ? {
                label: 'Back',
                onClick: handleGoBack,
                startIcon: <ArrowBackOutlined />,
              }
            : undefined
        }
        primaryAction={
          isLastStep
            ? {
                label: 'Done',
                onClick: filterProducts,
                endIcon: <CheckOutlined />,
                disabled: !currentStepHasValues,
              }
            : {
                label: 'Next',
                onClick: handleNext,
                endIcon: <ArrowForwardOutlined />,
                disabled: !currentStepHasValues,
              }
        }
      />
    </Dialog>
  );
};

export default HelpDialog;
